/* eslint-disable @bigbinary/neeto/use-webpack-alias */
import { useEffect, useState } from "react";

import { noop } from "@bigbinary/neeto-cist";
import { Button, Modal, Typography } from "@bigbinary/neetoui";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";

import { useUpdateTimeZone } from "./useTimeZoneApi";
import {
  checkIfTwoTimeZonesAreSame,
  getBrowserTimeZone,
  getUserTimeZone,
} from "./utils";

const TimezoneMismatchModal = ({ isOpen = false, onClose = noop }) => {
  const { t } = useTranslation();
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useState(false);
  const { isPending, mutate: updateTimeZone } = useUpdateTimeZone();
  const isUserAndBrowserTimeZoneSame = checkIfTwoTimeZonesAreSame(
    getUserTimeZone(),
    getBrowserTimeZone()
  );

  const handleUpdateTimeZone = () => {
    const payload = { timeZone: getBrowserTimeZone() };

    updateTimeZone(payload, {
      onSuccess: () => {
        setIsTimezoneModalOpen(false);
        onClose();
      },
    });
  };

  const handleCancel = () => {
    setIsTimezoneModalOpen(false);
    onClose();
  };

  useEffect(() => {
    if (!(process.env.RAILS_ENV !== "heroku")) return;
    setIsTimezoneModalOpen(!isUserAndBrowserTimeZoneSame);
  }, []);

  return (
    <Modal
      closeOnOutsideClick={false}
      isOpen={isTimezoneModalOpen || isOpen}
      size="large"
      onClose={handleCancel}
    >
      <Modal.Header>
        <Typography style="h2" weight="normal">
          {t("neetoMolecules.alert.timezone.title")}
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <span className="flex flex-col gap-y-4">
          <Typography component="span" style="body2">
            {t("neetoMolecules.alert.timezone.message", {
              entity: globalProps.appName,
            })}
          </Typography>
          <Typography component="span" style="h4">
            <Trans
              i18nKey="neetoMolecules.alert.timezone.userTimezone"
              values={{ entity: getUserTimeZone() }}
            />
          </Typography>
          <Typography component="span" style="h4">
            <Trans
              i18nKey="neetoMolecules.alert.timezone.browserTimezone"
              values={{ entity: getBrowserTimeZone() }}
            />
          </Typography>
        </span>
      </Modal.Body>
      <Modal.Footer className="flex items-center gap-x-2">
        <Button
          data-testid="update-timezone-button"
          disabled={isPending}
          label={t("neetoMolecules.alert.timezone.updateProfile")}
          loading={isPending}
          style="primary"
          onClick={handleUpdateTimeZone}
        />
        <Button
          label={t("neetoMolecules.common.actions.cancel")}
          style="text"
          onClick={handleCancel}
        />
      </Modal.Footer>
    </Modal>
  );
};

TimezoneMismatchModal.propTypes = {
  /**
   * Boolean value to toggle the modal.
   */
  isOpen: PropTypes.bool,
  /**
   * The function that's called after the modal is closed.
   */
  onClose: PropTypes.func,
};

export default TimezoneMismatchModal;
